import { useState } from "react";
import {
  AlignItems,
  Box,
  Color,
  ColorPreset,
  FontWeight,
  Glyph,
  H5,
  HoverEffect,
  Icon,
  Interpose,
  JustifyContent,
  PlainButton,
  Space,
  Text,
  XYGrid,
} from "@gocardless/flux-react";
import {
  BankAuthorisationResource,
  CustomerBankAccountResource,
} from "@gocardless/api/dashboard/types";
/* eslint-disable-next-line no-restricted-imports */
import { Trans } from "@lingui/macro";

import { getCustomerBankName } from "../../../../../../common/banks";

import BankDetailsDialog from "./bank-details-dialog/BankDetailsDialog";

import { Currency } from "src/common/currencies";

interface Props {
  accountName?: string;
  accountNumberEnding?: string;
  currency?: Currency;
  bankName?: string;
  handleUpdate: () => void;
  bankAuthorisation?: BankAuthorisationResource;
}

const BankDetailsCard: React.FC<Props> = ({
  accountName,
  accountNumberEnding,
  currency,
  handleUpdate,
  bankName,
  bankAuthorisation,
}) => {
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const { bankDisplayName, bankIconName } = getCustomerBankName({
    bank_name: bankName,
  } as CustomerBankAccountResource);

  const maskedAccountNumber = accountNumberEnding
    ? `*****${accountNumberEnding}`
    : null;
  const aisBankAccount = bankAuthorisation?.bank_accounts?.[0];

  const bankDetailsDialog = aisBankAccount ? (
    <BankDetailsDialog
      isOpen={openDetailsDialog}
      accountName={accountName}
      accountNumberEnding={aisBankAccount.account_number_ending}
      branchCodeEnding={aisBankAccount.branch_code_ending}
      currency={currency}
      accountBalance={aisBankAccount.balance}
      closeDialog={() => setOpenDetailsDialog(false)}
    />
  ) : (
    <></>
  );

  return (
    <Box
      bg={ColorPreset.BackgroundLight_01}
      borderRadius={1.5}
      borderColor={ColorPreset.BorderOnLight_05}
      borderWidth={1}
    >
      {bankDetailsDialog}
      <Box gutterH={2} gutterV={2}>
        <Interpose node={<Space v={1.5} />}>
          <Box
            alignItems={AlignItems.Start}
            justifyContent={JustifyContent.SpaceBetween}
            layout="flex"
          >
            <H5>
              {bankAuthorisation && !bankAuthorisation.failure_reason ? (
                <XYGrid
                  columnGap={0.75}
                  rowGap={0.75}
                  templateColumns={["auto", null, "auto auto"]}
                >
                  <Icon
                    name={Glyph.TickCircle}
                    color={Color.Success_300}
                    size="32px"
                  />
                  <Text>{currency} payout account added</Text>
                </XYGrid>
              ) : (
                <Trans id="setup.bank-details.currency-payout-account">
                  {currency} payout account
                </Trans>
              )}
            </H5>

            <PlainButton
              textDecoration="underline"
              effect={HoverEffect.TextDecoration}
              onClick={() => handleUpdate()}
            >
              <Text size={3} weight={FontWeight.Bold}>
                <Trans id="setup.bank-details.update">Update</Trans>
              </Text>
            </PlainButton>
          </Box>

          <Box>
            <Box spaceBelow={0.5}>
              <Text
                color={ColorPreset.TextOnLight_03}
                textTransform="uppercase"
                className="fs-mask"
              >
                {accountName}
              </Text>
            </Box>

            {maskedAccountNumber && (
              <Box>
                <Text className="fs-mask" color={ColorPreset.TextOnLight_03}>
                  <Trans id="setup.bank-details.account-number-ending">
                    Account number ending {maskedAccountNumber}
                  </Trans>
                  {aisBankAccount && (
                    <>
                      <Space layout="inline" h={0.5} />
                      {"|"}
                      <Space
                        layout="inline"
                        h={0.5}
                        css={{ display: "inline" }}
                      />
                      <PlainButton
                        effect={HoverEffect.TextDecoration}
                        textDecoration="none"
                        onClick={() => setOpenDetailsDialog(true)}
                      >
                        View data
                      </PlainButton>
                    </>
                  )}
                </Text>
              </Box>
            )}
          </Box>
          <Box layout="flex" flexDirection="row">
            <Interpose node={<Space layout="inline" h={0.25} />}>
              {bankIconName && <Icon name={bankIconName} size="24px" />}
              {bankDisplayName && (
                <Text textTransform="uppercase" weight={FontWeight.Bold}>
                  {bankDisplayName}
                </Text>
              )}
            </Interpose>
          </Box>
        </Interpose>
      </Box>
    </Box>
  );
};

export default BankDetailsCard;
